/**
 * Render quality: what a device can afford to draw, like Unreal's scalability
 * groups. A runtime setting (rule 12): a standalone player on a phone wants it
 * as much as the editor does. The backend applies it; nothing else reads it.
 *
 * It doesn't decide what content loads (a level's background environment is
 * a separate question, docs/planning/07-mobile-editor.md).
 */

export type QualityLevel = "high" | "mobile";

export interface RenderQuality {
  readonly level: QualityLevel;
  /** The highest device pixel ratio drawn at: fill rate is a phone's tightest limit. */
  readonly maxPixelRatio: number;
  /** Shadow map resolution per light, in texels. */
  readonly shadowMapSize: number;
  /** Selective bloom (its blur passes are several full-screen draws). */
  readonly bloom: boolean;
  /** Multisampled anti-aliasing on the canvas. */
  readonly antialias: boolean;
}

export const QUALITY: Readonly<Record<QualityLevel, RenderQuality>> = {
  high: { level: "high", maxPixelRatio: 2, shadowMapSize: 2048, bloom: true, antialias: true },
  // At a phone's density, 1.5x is still sharp, and its own pixels hide the jaggies MSAA would smooth.
  mobile: {
    level: "mobile",
    maxPixelRatio: 1.5,
    shadowMapSize: 1024,
    bloom: false,
    antialias: false,
  },
};

export interface QualityInput {
  /**
   * The main pointer is a finger (`pointer: coarse`): a phone or tablet, even
   * one that also has a stylus (which reports a fine pointer as well).
   */
  readonly touchFirst: boolean;
  /** What the address asks for (`?quality=`), if anything. */
  readonly requested: QualityLevel | null;
}

export function qualityFor({ touchFirst, requested }: QualityInput): RenderQuality {
  if (requested) return QUALITY[requested];
  return touchFirst ? QUALITY.mobile : QUALITY.high;
}

/** `?quality=high|mobile` overrides the device's, for measuring either one anywhere. */
export function qualityFromQuery(search: string): QualityLevel | null {
  const requested = new URLSearchParams(search).get("quality");
  return requested === "high" || requested === "mobile" ? requested : null;
}

/** The boot log's line: the level and what it means. */
export function describeQuality(quality: RenderQuality): string {
  return [
    quality.level,
    `${quality.maxPixelRatio}x pixels`,
    `${quality.shadowMapSize} shadows`,
    quality.bloom ? "bloom" : "no bloom",
    quality.antialias ? "MSAA" : "no MSAA",
  ].join(" · ");
}
